import { baseApi } from './baseApi'

import {
  BillingPatientStatementDeliveryMethod,
  BillingPatientStatementsList,
  BillingPatientStatementsListQuery,
  BillingPatientStatementWithDelivery,
  BillingStatementCycleStatus,
  OutstandingPatientBalanceSelection,
  OutstandingPatientBalancesList,
  OutstandingPatientBalancesQuery,
  PendingPatientStatementsList,
  PendingPatientStatementsListQuery,
  SendPatientStatementsDto,
  SendPatientStatementsResult,
  StatementRemindersList,
  StatementRemindersQuery,
} from 'store/types'

type GenerateStatementRequest = {
  patientId: number
  deliveryMethods: BillingPatientStatementDeliveryMethod[]
  sendNow?: boolean
}

type SendStatementRequest = {
  patientId: number
  statementId: string
  deliveryMethods: BillingPatientStatementDeliveryMethod[]
}

type BatchSendStatementRequest = {
  patientId: number
  statementIds: string[]
  deliveryMethods: BillingPatientStatementDeliveryMethod[]
}

export const patientStatementsApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    generatePatientStatement: builder.mutation<
      BillingPatientStatementWithDelivery,
      GenerateStatementRequest
    >({
      query: ({ patientId, ...body }) => ({
        url: `/lab/patients/${patientId}/account/statements`,
        method: 'POST',
        body,
      }),
    }),
    getPatientStatements: builder.query<
      BillingPatientStatementsList,
      BillingPatientStatementsListQuery
    >({
      query: ({ patientId, ...params }) => ({
        url: `/lab/patients/${patientId}/account/statements`,
        params,
      }),
      providesTags: ['PatientStatements'],
    }),
    getPatientStatementReminders: builder.query<StatementRemindersList, StatementRemindersQuery>({
      query: ({ patientId, ...params }) => ({
        url: `/lab/patients/${patientId}/account/statements/reminders`,
        params,
      }),
      providesTags: ['PatientStatements'],
    }),
    sendPatientStatement: builder.mutation<BillingPatientStatementWithDelivery, SendStatementRequest>({
      query: ({ patientId, statementId, ...body }) => ({
        url: `/lab/patients/${patientId}/account/statements/${statementId}/send`,
        method: 'POST',
        body,
      }),
      invalidatesTags: ['PatientStatements', 'PendingPatientStatements'],
    }),
    getPendingPatientStatements: builder.query<
      PendingPatientStatementsList,
      PendingPatientStatementsListQuery
    >({
      query: (params) => ({
        url: `/lab/patient-statements/pending`,
        params,
      }),
      providesTags: ['PendingPatientStatements'],
    }),
    batchSendPatientStatements: builder.mutation<SendPatientStatementsResult, BatchSendStatementRequest>({
      query: ({ patientId, ...body }) => ({
        url: `/lab/patients/${patientId}/account/statements/batch/send`,
        method: 'POST',
        body,
      }),
      invalidatesTags: ['PatientStatements', 'PendingPatientStatements'],
    }),
    // The whole selection in one request, across patients. Replaces a request
    // per patient that stopped at the first one who could not be reached.
    sendPatientStatements: builder.mutation<SendPatientStatementsResult, SendPatientStatementsDto>({
      query: (body) => ({
        url: `/lab/patient-statements/send`,
        method: 'POST',
        body,
      }),
      invalidatesTags: ['PatientStatements', 'PendingPatientStatements'],
    }),
    getStatementDownload: builder.query<
      { url: string },
      { patientId: number; statementId: string; download?: boolean }
    >({
      // `download` asks S3 for an attachment disposition. The lab listings omit
      // it and keep opening the PDF in a tab.
      query: ({ patientId, statementId, download }) => ({
        url: `/lab/patients/${patientId}/account/statements/${statementId}/download`,
        params: download ? { download: true } : undefined,
      }),
    }),
    getOutstandingPatientBalances: builder.query<
      OutstandingPatientBalancesList,
      OutstandingPatientBalancesQuery
    >({
      query: (params) => ({
        url: `/lab/patient-statements/outstanding`,
        params,
      }),
      providesTags: ['PatientStatements'],
    }),
    // Every patient the filters match, ids only — one request instead of
    // walking the listing a page at a time.
    getOutstandingPatientBalanceIds: builder.query<
      OutstandingPatientBalanceSelection[],
      OutstandingPatientBalancesQuery
    >({
      query: (params) => ({
        url: `/lab/patient-statements/outstanding/ids`,
        params,
      }),
      providesTags: ['PatientStatements'],
    }),
    printOutstandingBalancesSummary: builder.query<Blob, OutstandingPatientBalancesQuery>({
      query: (params) => ({
        url: `/lab/patient-statements/outstanding/summary/print`,
        params,
        headers: {
          Accept: 'application/pdf',
        },
        responseHandler: (response) => response.blob(),
      }),
    }),
    setStatementCycleStatus: builder.mutation<
      void,
      { patientId: number; status: BillingStatementCycleStatus }
    >({
      query: (body) => ({
        url: `/lab/patient-statements/cycle-status`,
        method: 'POST',
        body,
      }),
      invalidatesTags: ['PatientStatements'],
    }),
  }),
})

export const {
  useGeneratePatientStatementMutation,
  useGetPatientStatementsQuery,
  useGetPatientStatementRemindersQuery,
  useSendPatientStatementMutation,
  useBatchSendPatientStatementsMutation,
  useSendPatientStatementsMutation,
  useLazyGetStatementDownloadQuery,
  useGetPendingPatientStatementsQuery,
  useGetOutstandingPatientBalancesQuery,
  useLazyGetOutstandingPatientBalancesQuery,
  useLazyGetOutstandingPatientBalanceIdsQuery,
  useLazyPrintOutstandingBalancesSummaryQuery,
  useSetStatementCycleStatusMutation,
} = patientStatementsApi
